import { useCallback, useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material/styles';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { n8nManagerApiRef } from '@mercedes-benz/backstage-plugin-aiecosystem';
import { CostCenterCheckerUiOptions } from './schema';

const EXISTING_INSTANCE_WARNING =
  'An instance already exists for the cost centre. Review if a new one is required.';
const MIN_LENGTH_ERROR =
  'Error. Provide a cost center of at least 3 characters.';
const CHECKING_MESSAGE = 'Checking existing instances...';

const WARNING_COLORS = {
  light: '#A28400',
  dark: '#AF8E00',
};

type ValidationState = 'idle' | 'checking' | 'warning' | 'error';

export const CostCenterChecker = (
  props: FieldExtensionComponentProps<string, CostCenterCheckerUiOptions>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    schema: { title = 'Cost center (CC)', description },
    uiSchema,
    formData,
    formContext,
  } = props;

  const uiOptions =
    (uiSchema?.['ui:options'] as CostCenterCheckerUiOptions) ?? {};
  const debounceMs = uiOptions.debounceMs ?? 400;

  // When editing an existing instance the form is pre-filled with its urlPrefix.
  // We use that to exclude the instance from its own duplicate check.
  const currentUriPrefix: string | undefined =
    uiOptions.currentUriPrefix ?? formContext?.formData?.urlPrefix;

  const theme = useTheme();
  const warningColor =
    theme.palette.mode === 'dark' ? WARNING_COLORS.dark : WARNING_COLORS.light;

  const n8nManagerApi = useApi(n8nManagerApiRef);
  const [value, setValue] = useState<string>(formData ?? '');
  const [validationState, setValidationState] =
    useState<ValidationState>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkCostCenter = useCallback(
    (costCenter: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setValidationError(null);
      setValidationState('idle');

      if (!costCenter || costCenter.trim().length === 0) {
        onChange(undefined as any);
        return;
      }

      if (costCenter.trim().length < 3) {
        setValidationState('error');
        setValidationError(MIN_LENGTH_ERROR);
        onChange(undefined as any);
        return;
      }

      // Value is acceptable — pass upstream immediately (check is non-blocking)
      onChange(costCenter);
      setValidationState('checking');

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const result =
            await n8nManagerApi.checkExistingByCostCenter(costCenter);

          // In edit mode, only warn if another instance with a different URI
          // prefix shares this cost center (the current instance itself is not
          // a duplicate of itself).
          const isDuplicate = currentUriPrefix
            ? result.existingOrders.some(o => o.uriPrefix !== currentUriPrefix)
            : result.hasExistingOrders;

          setValidationState(isDuplicate ? 'warning' : 'idle');
        } catch {
          setValidationState('error');
          setValidationError(
            'Could not check existing instances right now. You can still proceed.',
          );
        }
      }, debounceMs);
    },
    [n8nManagerApi, debounceMs, onChange, currentUriPrefix],
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (formData && formData.trim().length > 0) {
      checkCostCenter(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setValue(inputValue);
    checkCostCenter(inputValue);
  };

  const hasRawErrors = rawErrors && rawErrors.length > 0;
  const hasWarning = validationState === 'warning';
  const isChecking = validationState === 'checking';
  const hasError = hasRawErrors || validationState === 'error';

  const renderEndAdornment = () => {
    if (isChecking) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} />
        </InputAdornment>
      );
    }
    if (hasWarning) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon sx={{ color: warningColor }} />
        </InputAdornment>
      );
    }
    if (hasError) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon color="error" />
        </InputAdornment>
      );
    }
    return undefined;
  };

  const getHelperText = () => {
    if (hasRawErrors) return rawErrors[0];
    if (validationError) return validationError;
    if (isChecking) return CHECKING_MESSAGE;
    if (hasWarning) return EXISTING_INSTANCE_WARNING;
    return description;
  };

  return (
    <Box>
      <TextField
        fullWidth
        label={title}
        required={required}
        value={value}
        onChange={handleInputChange}
        variant="standard"
        error={hasError}
        helperText={getHelperText()}
        InputLabelProps={
          hasWarning
            ? { sx: { color: `${warningColor} !important` } }
            : undefined
        }
        FormHelperTextProps={
          hasWarning ? { sx: { color: warningColor } } : undefined
        }
        InputProps={{
          endAdornment: renderEndAdornment(),
          ...(hasWarning && {
            sx: {
              '&:before': { borderBottomColor: `${warningColor} !important` },
              '&:after': { borderBottomColor: warningColor },
            },
          }),
        }}
        inputProps={{ maxLength: 255 }}
      />
    </Box>
  );
};
